// TransactionAnalyzer — the standard view for any set of published transactions.
//
// This is the destination component referenced by the `transaction-viewing`
// skill. It owns the print button, display options, filter panel, sankey
// chart, and the TransactionViewer table. Every surface that wants to show
// a list of transactions to the user — the Sankey/Transaction Analysis
// page, dashboard drilldowns, agent center-panel renders — should compose
// this component instead of building its own viewer.
//
// Props:
//   transactions      — array of enriched transactions (already flat, the
//                       caller is responsible for fetching + flattening).
//   isLoading         — true while the caller is still fetching.
//   mutate            — the SWR mutate from the underlying fetch (must be
//                       Page[] shape — both useGetEnrichedTransactions
//                       and useGetEnrichedTransactionsByIDListInfinite
//                       satisfy this; do not pass a flat-array mutate).
//   perspective       — sankey perspective ('CATEGORY', 'MERCHANT', etc.).
//   onPerspectiveChange — setter for perspective (orchestrator owns the
//                       URL <-> state binding).
//   drilldown         — null when showing the full case feed, OR
//                       { label: string, onClear: () => void } when the
//                       caller has narrowed the set. When provided, the
//                       banner renders and the filter panel opens by
//                       default so the user immediately sees the data is
//                       narrowed.
//   loadedCount, totalCount — optional progressive-load counters for the
//                       banner. When totalCount > loadedCount the banner
//                       shows "Showing M of N (loading...)".

import { useState, useRef, useContext, useEffect, useMemo } from "react";
import { useChannelSubscription } from "../../hooks/useAppSyncSubscription.js";
import UserProfileContext from "../../context/userprofilecontext.js";
import ReportCategorySankeyAnalysis, { perspectiveMap, tiersForPerspective } from '../ui/report-sankey-analysis.js';
import SankeyChartControls from '../ui/sankey-chart-controls.js';
import { TransactionViewer } from "../ui/TransactionViewer.js";
import TransactionDisplayOptionsForm from "../ui/TransactionDisplayOptionsForm.js";
import DataFilter from '../ui/filter/filtercontext.js';
import DateRangeFilter from '../ui/filter/filterdaterange.js';
import MultiSelectFilter from '../ui/filter/filtermultiselect.js';
import TextSearchFilter from '../ui/filter/filtertextsearch.js';
import DependentMultiSelectPairFilter from '../ui/filter/filterdependentmultiselectpair.js';
import CurrencyRangeFilter from '../ui/filter/filtercurrencyrange.js';
import TriStateBooleanFilter from "../ui/filter/filtertristateboolean.js";
import SwitchFilter from "../ui/filter/filterswitch.js";
import CollapsiblePanel from "../ui/CollapsiblePanel.js";
import usePDFPrintTransactions from "../ui/PDFPrintTransactions.js";
import { buildEstateWorkbook, downloadWorkbook, getTransactionListFileName, TRANSACTION_SHEET_PRINT_SETUP } from "../ui/estate-summary-excel-builder.js";
import { isCardRow } from "../../utils/cardholder.js";
import { Box } from "@mui/material";

export default function TransactionAnalyzer({
  transactions,
  isLoading,
  mutate,
  perspective,
  onPerspectiveChange,
  drilldown = null,
  loadedCount,
  totalCount,
}) {
  const isDrillDown = Boolean(drilldown);

  // Livi can write to a transaction (e.g. set_transaction_notes), which the
  // local SWR cache knows nothing about — without this the user has to refresh
  // to see a note the agent just told them it saved. The chat_agent publishes a
  // 'transactions' AppSync event keyed on the active case when it commits; ask
  // SWR to revalidate on it. Safe to pass an inline callback: the hook holds it
  // in a ref and only re-subscribes on [sessionId, channel, enabled].
  const { userProfile } = useContext(UserProfileContext);
  const activeCaseId = userProfile?.currentCase?.caseID || null;
  useChannelSubscription(
    activeCaseId,
    'transactions',
    () => { mutate?.(); },
    { enabled: !!activeCaseId && !!mutate },
  );

  const [selectedTransactions, setSelectedTransactions] = useState(transactions || []);
  const [filteredData, setFilteredData] = useState(transactions || []);
  const defaultChartDisplay = false;
  const [displayOptions, setDisplayOptions] = useState({
    isDraft: true,
    printChart: defaultChartDisplay,
    printTransactions: true,
    showMerchant: true,
    showSubcategory: true,
    showFlag: true,
    showSource: true,
    showNotes: true,
    showCardholder: true,
    maxNodesPerType: 10,
  });

  // Cardholder column/filter only when some row was charged on a card: a case
  // with no credit cards looks exactly as before (cardholder-reporting.md).
  // One gate for the table, PDF and Excel so an export matches the screen.
  const hasCardRows = useMemo(() => (transactions || []).some(isCardRow), [transactions]);
  const effectiveDisplayOptions = useMemo(
    () => ({ ...displayOptions, showCardholder: Boolean(displayOptions.showCardholder && hasCardRows) }),
    [displayOptions, hasCardRows],
  );

  // Tiered-Sankey display config (chart-only state, parallels displayOptions —
  // deliberately NOT lifted to the URL like `perspective`). `enabledTiers` is
  // seeded from the active perspective preset; `separateAccounts` false
  // collapses all accounts into a single "All Accounts" center node.
  const [enabledTiers, setEnabledTiers] = useState(() => new Set(tiersForPerspective(perspective)));
  const [separateAccounts, setSeparateAccounts] = useState(true);

  // Re-seed the tier checkboxes when the perspective (URL/preset) changes.
  useEffect(() => {
    setEnabledTiers(new Set(tiersForPerspective(perspective)));
  }, [perspective]);

  const tierSetKey = (tiers) => [...tiers].sort().join(',');
  const currentTierKey = tierSetKey(enabledTiers);
  const presetValue = Object.keys(perspectiveMap)
    .find((k) => tierSetKey(new Set(perspectiveMap[k].tiers)) === currentTierKey) || 'CUSTOM';

  const handlePresetChange = (key) => {
    if (key === 'CUSTOM') return;
    setEnabledTiers(new Set(tiersForPerspective(key)));
    onPerspectiveChange?.(key);
  };
  const handleToggleTier = (key) => {
    setEnabledTiers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Open the filter panel by default when the caller has narrowed the set so
  // the user immediately sees the data is filtered (otherwise they may think
  // the page is broken — small row count looks like a bug).
  const filtersDefaultCollapsed = !isDrillDown;
  const [filtersDescription, setFiltersDescription] = useState("");
  const sankeyRef = useRef(null);

  // Re-key DataFilter on drilldown identity so leaving a drilldown (or
  // switching between drilldowns) resets all filter selections. Without
  // this, MultiSelect filters auto-select every value present in the
  // narrow drilldown set; when the user clears the drilldown those stale
  // selections persist and silently re-narrow the full feed.
  const filterScopeKey = drilldown?.label || 'full';

  // Mirror the table's sort order so PDF print emits rows in the order the
  // user is currently viewing. TransactionViewer pushes its sorted memo up
  // via onSortedTransactionsChange. Falls back to selectedTransactions
  // before the first emit (e.g., the user prints before the table renders).
  const [sortedPrintableTransactions, setSortedPrintableTransactions] = useState([]);
  const printableTransactions = sortedPrintableTransactions.length > 0
    ? sortedPrintableTransactions
    : selectedTransactions;

  // The table's sort order in words ("Subcategory (A-Z), then Merchant (A-Z)"),
  // captioned on the printed report so the row order is self-explanatory.
  const [sortDescription, setSortDescription] = useState('');

  const handlePrint = usePDFPrintTransactions({
    transactions: printableTransactions,
    displayOptions: effectiveDisplayOptions,
    filtersDescription,
    sortDescription,
    chartRef: sankeyRef,
  });

  // Export the currently filtered/sorted transaction set (exactly what the
  // table shows) to a single-sheet Excel workbook, reusing the Estate Report's
  // Transactions-sheet builder. The Account column carries each transaction's
  // source account display name.
  const [exportingExcel, setExportingExcel] = useState(false);
  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const wb = buildEstateWorkbook({
        transactions: printableTransactions,
        displayOptions: effectiveDisplayOptions,
        selectedSheets: new Set(['Transactions']),
        // Provenance for the "Report Info" sheet — the row order is deliberate
        // and a recipient can re-sort the grid without knowing that.
        sortDescription,
        filtersDescription,
      });
      await downloadWorkbook(wb, getTransactionListFileName(), TRANSACTION_SHEET_PRINT_SETUP);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Excel export failed:', err);
      }
    } finally {
      setExportingExcel(false);
    }
  };

  const handleFilteredDataChange = (newFilteredData) => {
    setFilteredData(newFilteredData);
    setSelectedTransactions(newFilteredData);
  };

  const handleCollapseSankey = () => {
    setSelectedTransactions(filteredData);
    setDisplayOptions((prev) => ({
      ...prev,
      printChart: false,
      printTransactions: true,
    }));
  };

  const handleExpandSankey = () => {
    setDisplayOptions((prev) => ({ ...prev, printChart: true }));
  };

  const showProgressiveCount = isDrillDown
    && typeof totalCount === 'number'
    && typeof loadedCount === 'number'
    && loadedCount < totalCount;

  // Banner shown when the caller has narrowed the dataset. Makes the
  // narrowed state visible AND provides a one-click escape back to the
  // unfiltered view. Required for forensic-trust UX — without it, users
  // assume the page is broken.
  const drillDownBanner = isDrillDown ? (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        p: 'var(--space-3)',
        mb: 'var(--space-4)',
        bgcolor: 'var(--color-accent-50, #f0fdfa)',
        border: '1px solid var(--color-accent-200, #99f6e4)',
        borderRadius: 'var(--radius-md)',
        color: 'var(--color-accent-800, #115e59)',
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--text-sm)',
      }}
    >
      <span>
        Showing{' '}
        <strong>
          {showProgressiveCount
            ? `${loadedCount} of ${totalCount}`
            : (typeof totalCount === 'number' ? totalCount : (transactions?.length ?? '…'))}
        </strong>{' '}
        transactions: <strong>{drilldown.label}</strong>
        {showProgressiveCount && ' (loading…)'}
      </span>
      <Box
        component="button"
        type="button"
        onClick={drilldown.onClear}
        sx={{
          background: 'none',
          border: '1px solid var(--color-accent-300, #5eead4)',
          color: 'inherit',
          padding: 'var(--space-1) var(--space-3)',
          borderRadius: 'var(--radius-sm)',
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-sm)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          '&:hover': { background: 'var(--color-accent-100, #ccfbf1)' },
          '&:focus-visible': { outline: 'none', boxShadow: 'var(--focus-ring)' },
        }}
      >
        Clear filter
      </Box>
    </Box>
  ) : null;

  return (
    <>
      {drillDownBanner}
      <Box sx={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', mb: 'var(--space-6)' }}>
        <button
          onClick={handlePrint}
          style={{
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
          }}
        >
          🖨️ Print / Save as PDF
        </button>
        <button
          onClick={handleExportExcel}
          disabled={exportingExcel}
          aria-label="Export transactions to Excel"
          style={{
            backgroundColor: '#2e7d32',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '4px',
            cursor: exportingExcel ? 'default' : 'pointer',
            opacity: exportingExcel ? 0.7 : 1,
            fontSize: '16px',
          }}
        >
          {exportingExcel ? 'Exporting…' : '⬇️ Export to Excel'}
        </button>
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <TransactionDisplayOptionsForm
          displayOptions={displayOptions}
          setDisplayOptions={setDisplayOptions}
          hasCardRows={hasCardRows}
        />
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <DataFilter
          key={filterScopeKey}
          data={transactions}
          onFilteredDataChange={handleFilteredDataChange}
          onFiltersDescriptionChange={setFiltersDescription}
          title="Filter Transactions"
          defaultCollapsed={filtersDefaultCollapsed}
        >
          <DateRangeFilter field="transactionDate" label="Transaction Date" />
          <MultiSelectFilter field='bankAccountName' label="Bank Account" />
          {/* Always mounted, hidden when no row is on a card: a filter never
              unregisters, so unmounting it when hasCardRows flips would leave
              its last selection applied. Untouched, it selects everything. */}
          <div hidden={!hasCardRows}>
            <DependentMultiSelectPairFilter
              primaryField='cardholderName'
              secondaryField='cardLabel'
              primaryLabel="Cardholder"
              secondaryLabel="Card"
              label="Cardholder"
            />
          </div>
          <TextSearchFilter fields={['description']} label="Description" />
          <CurrencyRangeFilter field="amount" label="Amount" unit="$" />
          <DependentMultiSelectPairFilter
            primaryField='categoryName'
            secondaryField='subcategoryName'
            primaryLabel="Category"
            secondaryLabel="Subcategory"
            label="Transaction Category"
          />
          {/* Ledger = the chart a row is categorized on (Personal / Business /
              Investment). Same-named categories exist in more than one ledger,
              so this also disambiguates the category filter. */}
          <MultiSelectFilter field='ledgerName' label="Ledger" />
          <MultiSelectFilter field='merchantName' label="Merchant" />
          <MultiSelectFilter field='flagName' label="Flag" />
          <TriStateBooleanFilter
            field="hasTransferPartner"
            label="Internal Transfers"
            allLabel="All"
            trueLabel="Only"
            falseLabel="Exclude"
          />
          {/* Narrows WITHIN transfers to the ones still awaiting a human verdict
              (TRANSFER edge disposition === 'PROPOSED'); CONFIRMED pairs drop out.
              Composes with the tri-state above rather than replacing it — note
              that "Internal Transfers: Exclude" plus this switch is an empty set
              by construction, since a non-transfer can never be proposed. */}
          <SwitchFilter
            field="isProposedTransfer"
            label="Proposed Transfers Only"
          />
          {/* Rows categorized on a different ledger than their account's —
              personal spending in a business account, business activity in a
              personal one. Each is a forensic finding (categorization segments
              Phase 3). */}
          <SwitchFilter
            field="isCrossLedger"
            label="Cross-ledger only"
          />
        </DataFilter>
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <CollapsiblePanel
          title="Sankey Analysis: Flow of Funds"
          defaultCollapsed={!defaultChartDisplay}
          onCollapse={handleCollapseSankey}
          onExpand={handleExpandSankey}
        >
          {/* Shared centered container so the controls and the chart line up on
              the same width/edges (matches the prototype). */}
          <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto' }}>
            <SankeyChartControls
              presetValue={presetValue}
              onPresetChange={handlePresetChange}
              enabledTiers={enabledTiers}
              onToggleTier={handleToggleTier}
              separateAccounts={separateAccounts}
              onToggleSeparate={setSeparateAccounts}
            />
            <ReportCategorySankeyAnalysis
              ref={sankeyRef}
              data={filteredData}
              isLoading={isLoading}
              onSelectData={setSelectedTransactions}
              enabledTiers={enabledTiers}
              separateAccounts={separateAccounts}
              displayOptions={displayOptions}
            />
          </Box>
        </CollapsiblePanel>
      </Box>

      {/* Transaction table: fixed height so virtual scroller has room,
          flexShrink: 0 so it isn't squeezed when panels above expand.
          contentArea's overflow-y handles page-level scrolling. */}
      <Box sx={{
        flexShrink: 0,
        height: '70vh',
        minHeight: 400,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <TransactionViewer
          transactions={selectedTransactions}
          isLoading={isLoading}
          displayOptions={effectiveDisplayOptions}
          mutateTransactions={mutate}
          onSortedTransactionsChange={setSortedPrintableTransactions}
          onSortDescriptionChange={setSortDescription}
        />
      </Box>
    </>
  );
}
